import { Link } from 'react-router'

import { elapsedText, formatMoney, formatPnl, isEnded, paperPnl, positionText } from '@/app/lib/quant/liveView'
import type { LiveSession } from '@/app/lib/quant/types'
import { returnTone, TONE_CLASS } from '@/ui/lib/quantFormat'
import { QuantLiveBadge } from '@/ui/QuantLiveBadge'

export interface QuantLiveRowProps {
  session: LiveSession
  /** The pane's 1 s tick (`useNowTick`); elapsed time is derived from it, never counted. */
  now: number
}

/** Equity as the list shows it: the final figure once ended, else the last state's. */
function rowEquity(session: LiveSession): number | null | undefined {
  return isEnded(session) && session.final ? session.final.equity : session.state?.account?.equity
}

/**
 * One live session in the Live tab (spec 041 P3): a fixed 64 px link, two lines.
 * Line one is the status badge, `strategy · instrument` and the position; line two
 * is elapsed time, equity and paper P&L. Every cell has a fixed width and truncates
 * with the full text in `title`, so no server value changes the row's size. The row
 * never applies the "figures are old" rule (the detail does).
 */
export function QuantLiveRow({ session, now }: QuantLiveRowProps) {
  const name = `${session.strategy_slug} · ${session.instrument}`
  const position = positionText(session.state?.position, session.instrument)
  const elapsed = elapsedText(session, now)
  const equity = `equity ${formatMoney(rowEquity(session))}`
  const pnl = paperPnl(session)
  const pnlText = pnl === null ? '— paper' : `${formatPnl(pnl)} paper`
  return (
    <Link
      to={`/strategies/${session.strategy_slug}/live/${session.id}`}
      aria-label="Open live session"
      data-live-row={session.id}
      className="box-border flex h-[64px] w-full flex-col justify-center gap-1.5 overflow-hidden border border-paper-14 px-3 font-mono hover:border-paper-35"
    >
      <span className="flex min-w-0 items-center gap-3">
        <QuantLiveBadge status={session.status} />
        <span className="min-w-0 flex-1 truncate text-[12.5px]" title={name}>
          {name}
        </span>
        <span className="w-[220px] shrink-0 truncate text-right text-[12.5px]" title={position}>
          {position}
        </span>
      </span>
      <span className="flex min-w-0 items-center gap-3 pl-[104px] text-[11px] text-paper-50">
        <span className="w-[110px] shrink-0 truncate" title={elapsed}>
          {elapsed}
        </span>
        <span className="w-[170px] shrink-0 truncate" title={equity}>
          {equity}
        </span>
        <span className={`min-w-0 flex-1 truncate ${TONE_CLASS[returnTone(pnl)]}`} title={pnlText}>
          {pnlText}
        </span>
      </span>
    </Link>
  )
}
